import { useState, useSyncExternalStore } from 'react';
import { api } from '../api';
import { imprimirEtiquetas } from '../lib/imprimir';
import { anotarRollo, estadoRollo, ROLLO, sacarPendientes, suscribir } from '../lib/rollo';
import { mensajeDeError } from '../lib/useApi';
import { aviso, botonPrimario, botonSecundario, campo, chip } from '../theme';
import { Icono, useConfirmar } from './ui';

/** Con cuántas se avisa que el rollo se está terminando: el mismo límite que la campana. */
const POCAS = 20;

/**
 * El rollo de la térmica, arriba de las pantallas de imprimir: cuántas
 * etiquetas le quedan, el cambio de rollo y las que quedaron pendientes de una
 * tirada que no entró. Cambiar el rollo es de quien está en la impresora, no
 * del admin: por eso se carga acá y no sólo en Mantenimiento.
 */
export default function RolloEtiquetas() {
  const { quedan, pendientes } = useSyncExternalStore(suscribir, estadoRollo);
  const [cambiando, setCambiando] = useState(false);
  const [nuevo, setNuevo] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [mensaje, setMensaje] = useState<{ texto: string; ok: boolean } | null>(null);
  const [confirmar, dialogo] = useConfirmar();

  const cuantasPendientes = pendientes.reduce((n, p) => n + p.dibujos.length, 0);

  async function guardarRollo() {
    setOcupado(true);
    setMensaje(null);
    try {
      anotarRollo((await api.cambiarRollo(Number(nuevo))).rolloEtiquetas);
      setCambiando(false);
      setNuevo('');
    } catch (err) {
      setMensaje({ texto: mensajeDeError(err), ok: false });
    } finally {
      setOcupado(false);
    }
  }

  function imprimirPendientes() {
    const tiradas = sacarPendientes();
    const nombres = [...new Set(tiradas.map((t) => t.nombre))].join(', ');
    // Las que tampoco entren en este rollo vuelven a quedar pendientes solas.
    const problema = imprimirEtiquetas({ nombre: `Pendientes: ${nombres}`, ...ROLLO }, tiradas.flatMap((t) => t.dibujos));
    setMensaje(problema ? { texto: problema, ok: false } : { texto: 'Salieron las etiquetas pendientes.', ok: true });
  }

  async function descartar() {
    const ok = await confirmar({
      titulo: `¿Descartar ${cuantasPendientes} etiquetas pendientes?`,
      detalle: 'No se imprimen. Si después hacen falta, hay que volver a generarlas desde su pantalla.',
      boton: 'Descartar',
    });
    if (!ok) return;
    sacarPendientes();
    setMensaje(null);
  }

  if (quedan === null && cuantasPendientes === 0) return null;
  const t = quedan === 0 ? 'err' : quedan !== null && quedan <= POCAS ? 'warn' : 'neu';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8, marginBottom: 18 }}>
      {dialogo}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
        {quedan !== null && (
          <span style={chip(t)}>
            <Icono nombre="label" size={16} />
            Rollo 100 × 150: quedan {quedan}
          </span>
        )}
        {cambiando ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void guardarRollo();
            }}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <input
              aria-label="Etiquetas que trae el rollo nuevo"
              type="number"
              inputMode="numeric"
              min={0}
              step={1}
              required
              autoFocus
              placeholder="Trae…"
              value={nuevo}
              onChange={(e) => setNuevo(e.target.value)}
              style={{ ...campo, width: 100, minHeight: 34 }}
            />
            <button type="submit" disabled={ocupado || nuevo === ''} style={{ ...botonPrimario, minHeight: 34, padding: '0 12px' }}>
              Guardar
            </button>
            <button type="button" onClick={() => setCambiando(false)} style={{ ...botonSecundario, minHeight: 34, padding: '0 12px' }}>
              Cancelar
            </button>
          </form>
        ) : (
          <button type="button" onClick={() => setCambiando(true)} style={{ ...botonSecundario, minHeight: 34, padding: '0 12px' }}>
            Cambié el rollo
          </button>
        )}
      </div>

      {cuantasPendientes > 0 && (
        <div style={{ ...aviso('warn'), display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ flex: 1, minWidth: 200 }}>
            {cuantasPendientes} {cuantasPendientes === 1 ? 'etiqueta quedó pendiente' : 'etiquetas quedaron pendientes'} porque
            no entraban en el rollo ({[...new Set(pendientes.map((p) => p.nombre))].join(', ')}).
          </span>
          <button
            type="button"
            onClick={imprimirPendientes}
            disabled={quedan === 0}
            title={quedan === 0 ? 'Primero cambiá el rollo' : undefined}
            style={{ ...botonPrimario, minHeight: 34, padding: '0 12px' }}
          >
            Imprimir pendientes
          </button>
          <button type="button" onClick={() => void descartar()} style={{ ...botonSecundario, minHeight: 34, padding: '0 12px' }}>
            Descartar
          </button>
        </div>
      )}

      {mensaje && <div style={aviso(mensaje.ok ? 'ok' : 'err')}>{mensaje.texto}</div>}
    </div>
  );
}
